/* Breathing-rhythm driver for the masthead smoke. Replaces tsparticles'
   static emitters with a per-frame loop that:

     - Computes intensity = (sin(2π t / period) + 1) / 2 — smooth gradient
       in [0, 1]. The peak (1) and trough (0) are hit only at infinitesimal
       moments; the rest of the cycle is a continuous ramp.
     - Spawns particles at a rate proportional to intensity (zero only at
       the trough instant).
     - Sets each new particle's initial velocity to a "down-and-outward"
       vector scaled by intensity, so peak-of-cycle particles get max
       thrust and trough-side particles drift upward on buoyancy alone.

   The smoke template (shape, opacity/size animations, gravity, life)
   still lives in mastheadConfigs.SMOKE_OPTIONS; this module just owns
   spawning and initial velocity. */

import type { Container } from "@tsparticles/engine";
import {
  MASTHEAD_REFERENCE_WIDTH,
  SMOKE_GRAVITY,
  SMOKE_SIZE_GROW_SPEED,
  SMOKE_SIZE_MAX,
  SMOKE_SIZE_MIN,
} from "./mastheadConfigs";

const BREATH_PERIOD_S = 10; // full inhale + exhale cycle
// Magnitude of the particle.velocity vector at sine peak (calibrated at
// MASTHEAD_REFERENCE_WIDTH). Subtle — peak particles get a
// barely-visible nudge outward; trough particles get nothing and just
// float up. tsparticles multiplies this by move.speed (1) ÷ 2 per
// frame, so PEAK_THRUST × 30 ≈ peak px/s. We multiply by
// canvas-relative scale at spawn so the plume covers the same fraction
// of the masthead regardless of rendered size.
const PEAK_THRUST = 3;
const PEAK_RATE_PER_NOSTRIL = 200; // particles/sec/nostril at sine peak
// Fraction of the (sine + 1) / 2 range below which emission is zero.
// 0 → full sine (every part of the cycle emits something).
// 0.5 → bottom half of the sine is quiescent; emission tapers smoothly
//       to 0 as the sine descends through the threshold, holds at 0,
//       then tapers smoothly back up on the way out. Attenuation, not
//       a gate — the slope hits 0 at the threshold rather than stepping.
// 0.9 → only the top 10 % of the cycle emits.
// Clamp to [0, 1).
const QUIESCENT_FRACTION = 0.5;

/** Pixel coords from briefs/particle-emitters.md, normalised to the
    1600×640 frame. `dirDeg` in screen coords (0° = right, 90° = down). */
const NOSTRILS: ReadonlyArray<{
  xPct: number;
  yPct: number;
  /** Outward-and-downward thrust direction for this nostril's head. */
  dirDeg: number;
}> = [
  // Left dragon — exhales to the right (toward the logotype) and down.
  { xPct: 297 / 16, yPct: 257 / 6.4, dirDeg: 60 },
  { xPct: 353 / 16, yPct: 244 / 6.4, dirDeg: 60 },
  // Right dragon — exhales to the left and down.
  { xPct: 1267 / 16, yPct: 247 / 6.4, dirDeg: 120 },
  { xPct: 1326 / 16, yPct: 250 / 6.4, dirDeg: 120 },
];

export type BreathingHandle = { stop: () => void };

/** Start the cycle at t = BREATH_PERIOD_S / 2 — the boundary between
    the (active) exhale half and the (quiescent) inhale half. With
    QUIESCENT_FRACTION = 0.5, the entire inhale half emits zero
    particles, so passing this as `phaseOffsetS` produces a full
    BREATH_PERIOD_S / 2 seconds of silence before the first puff. */
export const BREATHING_INHALE_OFFSET_S = BREATH_PERIOD_S / 2;

export function startBreathing(
  container: Container,
  options: { phaseOffsetS?: number } = {},
): BreathingHandle {
  const phaseOffsetS = options.phaseOffsetS ?? 0;
  let rafId = 0;
  let stopped = false;
  const start = performance.now();
  let last = start;
  // Fractional accumulator so low-intensity emission averages out over
  // many frames instead of dropping particles to integer rounding.
  let accumulator = 0;

  const tick = (now: number) => {
    if (stopped) return;
    const dt = Math.min(0.1, (now - last) / 1000); // clamp big dt (tab refocus)
    last = now;

    const t = (now - start) / 1000 + phaseOffsetS;
    // Smooth gradient in [0, 1]: peak at sin=1, trough at sin=-1 → 0.
    // Both extrema are hit only instantaneously; the curve is a
    // continuous sinusoid in between.
    const raw = (Math.sin((2 * Math.PI * t) / BREATH_PERIOD_S) + 1) / 2;
    // Quiescent attenuation: linearly remap [Q, 1] → [0, 1] and clamp
    // anything below Q to 0. Approaching Q from above, intensity
    // smoothly tapers to 0; below Q, no emission. The slope changes at
    // the threshold but the value doesn't step.
    const intensity =
      QUIESCENT_FRACTION >= 1
        ? 0
        : Math.max(0, (raw - QUIESCENT_FRACTION) / (1 - QUIESCENT_FRACTION));

    accumulator +=
      intensity * PEAK_RATE_PER_NOSTRIL * NOSTRILS.length * dt;

    const w = container.canvas.size.width;
    const h = container.canvas.size.height;
    // CSS-pixel scale: the constants are calibrated at
    // MASTHEAD_REFERENCE_WIDTH (CSS px); a smaller canvas gets
    // proportionally smaller per-particle velocity, gravity, and
    // size so the visual plume fills the same fraction of the dragon
    // at every size. tsparticles' canvas.size is in DEVICE pixels
    // (CSS × pixelRatio) — we divide it back out so the calibration
    // is independent of the device's DPI.
    const pxRatio = container.retina.pixelRatio || 1;
    const cssW = w / pxRatio;
    const scale = cssW > 0 ? cssW / MASTHEAD_REFERENCE_WIDTH : 1;
    const sizeOverride = {
      size: {
        value: { min: SMOKE_SIZE_MIN * scale, max: SMOKE_SIZE_MAX * scale },
        animation: { speed: SMOKE_SIZE_GROW_SPEED * scale },
      },
      move: { gravity: { acceleration: SMOKE_GRAVITY * scale } },
    };

    while (accumulator >= 1) {
      const nostril = NOSTRILS[Math.floor(Math.random() * NOSTRILS.length)]!;
      const position = {
        x: (nostril.xPct / 100) * w,
        y: (nostril.yPct / 100) * h,
      };
      const speed = PEAK_THRUST * intensity * scale;
      const angle = (nostril.dirDeg * Math.PI) / 180;
      const particle = container.particles.addParticle(position, sizeOverride);
      if (particle) {
        particle.velocity.x = Math.cos(angle) * speed;
        particle.velocity.y = Math.sin(angle) * speed;
      }
      accumulator -= 1;
    }

    rafId = requestAnimationFrame(tick);
  };

  rafId = requestAnimationFrame(tick);

  return {
    stop: () => {
      stopped = true;
      if (rafId) cancelAnimationFrame(rafId);
    },
  };
}
